/**
 * IntroTutorial — a minimal "first visit" walkthrough dialog.
 *
 * Use it to give therapists a 30-second tour the first time they land on
 * a complex page. The "seen" state is persisted to localStorage under
 * a caller-supplied key so it never re-prompts on subsequent visits.
 *
 * Props:
 *   storageKey  — required, unique per tutorial (e.g. "tutorial-assessments-v1")
 *   title       — dialog heading
 *   steps       — [{title, body, icon?}, ...]  Each step gets its own slide.
 *   buttonLabel — final-step CTA (default "Got it")
 *   showHelpButton — also render a small "?" button so the therapist can
 *                    reopen the tutorial on demand.
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { HelpCircle, ChevronRight, X } from 'lucide-react';

export default function IntroTutorial({
  storageKey,
  title = 'Quick tour',
  steps = [],
  buttonLabel = 'Got it — let\u2019s go',
  showHelpButton = true,
  testid = 'intro-tutorial',
}) {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!storageKey) return;
    try {
      const seen = localStorage.getItem(storageKey);
      if (!seen) setOpen(true);
    } catch { /* noop */ }
  }, [storageKey]);

  const close = () => {
    setOpen(false);
    try { localStorage.setItem(storageKey, '1'); } catch { /* noop */ }
  };

  const reopen = () => {
    setIdx(0);
    setOpen(true);
  };

  const step = steps[idx] || { title: '', body: '' };
  const isLast = idx === steps.length - 1;

  return (
    <>
      {showHelpButton && (
        <button
          type="button"
          onClick={reopen}
          className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-primary transition-colors px-2.5 py-1 rounded-full hover:bg-stone-100"
          data-testid={`${testid}-reopen`}
          aria-label="Replay tutorial"
        >
          <HelpCircle className="w-3.5 h-3.5" />
          Replay tour
        </button>
      )}

      <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden" data-testid={testid}>
          <DialogTitle className="sr-only">{title}</DialogTitle>
          <DialogDescription className="sr-only">{step.body}</DialogDescription>
          {/* Progress dots */}
          <div className="px-6 pt-5 pb-2 flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider text-primary/70 font-semibold">
              {title}
            </span>
            <div className="flex items-center gap-1.5">
              {steps.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx ? 'w-6 bg-primary' : 'w-1.5 bg-stone-200'
                  }`}
                />
              ))}
              <button
                type="button"
                onClick={close}
                className="ml-2 text-stone-400 hover:text-stone-700"
                aria-label="Skip tour"
                data-testid={`${testid}-skip`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.18 }}
              className="px-6 pb-4 pt-2"
            >
              {step.icon && (
                <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-3">
                  {step.icon}
                </div>
              )}
              <h2 className="text-lg font-serif text-stone-900 mb-2">{step.title}</h2>
              <p className="text-sm text-stone-600 leading-relaxed whitespace-pre-line">
                {step.body}
              </p>
            </motion.div>
          </AnimatePresence>

          <div className="bg-stone-50 px-6 py-4 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={close}
              className="text-sm text-stone-500 hover:text-stone-700"
              data-testid={`${testid}-skip-text`}
            >
              Skip
            </button>
            {isLast ? (
              <Button
                onClick={close}
                className="rounded-full"
                data-testid={`${testid}-done`}
              >
                {buttonLabel}
              </Button>
            ) : (
              <Button
                onClick={() => setIdx(idx + 1)}
                className="rounded-full"
                data-testid={`${testid}-next`}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
